import { CarePlanNoteResponse } from '~/lib/types/carePlan/carePlans';
import { partitionProblemsForNote } from '~/lib/util/carePlan.util';
import { PatientReadonlyNoteCollapse } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientReadonlyNoteCollapse';
import { ChakraBox, ChakraFlex, ChakraGrid, Text } from '@equip.health/ui';
import { HiddenCoOccurringProblemsActionBar } from '~/components/maud/patients/note/HiddenCoOccurringProblemsActionBar';
import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PatientNoteItemCarePlanGoal } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteItemCarePlanGoal';
import { PatientNoteItemCarePlanQolGoal } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteItemCarePlanQolGoal';
import { useFilteredCarePlanNoteInterventions } from '~/lib/hooks/care-team/useFilteredCarePlanNoteInterventions';
import { PatientNoteItemInterventionRow } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteItemInterventionRow';

type Props = {
  carePlan: CarePlanNoteResponse;
  isSigned: boolean;
  showGoals: boolean;
  showInterventions: boolean;
};
export const PatientNoteItemCarePlanSection = ({
  carePlan,
  isSigned,
  showGoals,
  showInterventions,
}: Props) => {
  const {
    carePlanM4MedPsychNotes: isCarePlanM4MedPsychNotesEnabled,
    carePlanM4MedPsychProblems,
  } = useFlags<LDFlags>();

  const { edProblems, coOccurringProblems, hiddenCoOccurringCount } = useMemo(
    () =>
      partitionProblemsForNote({
        problems: carePlan.problems,
        isMedPsychProblemsEnabled: carePlanM4MedPsychProblems,
        compare: (a, b) =>
          a.pathwayProblem.sortOrder - b.pathwayProblem.sortOrder,
      }),
    [carePlan.problems, carePlanM4MedPsychProblems],
  );

  const shouldShowEdGoalsSection = isCarePlanM4MedPsychNotesEnabled
    ? showGoals && edProblems.length > 0
    : showGoals && (edProblems.length > 0 || carePlan.qolGoals.length > 0);
  // The hidden-problems label is for notes that can still be edited; signed
  // notes are a fixed record.
  const shouldShowHiddenCoOccurringLabel =
    !isSigned && hiddenCoOccurringCount > 0;
  const shouldShowCoOccurringSection =
    isCarePlanM4MedPsychNotesEnabled &&
    showGoals &&
    (coOccurringProblems.length > 0 || shouldShowHiddenCoOccurringLabel);
  const shouldShowQolGoalsSection =
    isCarePlanM4MedPsychNotesEnabled &&
    showGoals &&
    carePlan.qolGoals.length > 0;

  const interventions = useFilteredCarePlanNoteInterventions(carePlan);

  return (
    <PatientReadonlyNoteCollapse title="Care plan">
      {shouldShowEdGoalsSection && (
        <>
          <Text variant="bodyMedium">
            Treatment goals & associated behaviors
          </Text>
          <ChakraFlex
            borderRadius="12px"
            borderWidth="1px"
            direction="column"
            marginBottom="16px"
            marginTop="12px"
            padding="18px"
            rowGap="16px"
          >
            {edProblems.map((problem) => (
              <PatientNoteItemCarePlanGoal key={problem.id} problem={problem} />
            ))}
            {!isCarePlanM4MedPsychNotesEnabled &&
              carePlan.qolGoals.map((qolGoal) => (
                <PatientNoteItemCarePlanQolGoal
                  key={qolGoal.id}
                  qolGoal={qolGoal}
                />
              ))}
          </ChakraFlex>
        </>
      )}
      {shouldShowCoOccurringSection && (
        <>
          <Text variant="bodyMedium">Co-occurring problems</Text>
          {shouldShowHiddenCoOccurringLabel && (
            <ChakraBox
              marginBottom={coOccurringProblems.length > 0 ? 0 : '16px'}
              marginTop="12px"
            >
              <HiddenCoOccurringProblemsActionBar
                count={hiddenCoOccurringCount}
                id="patient-note-item-hidden-co-occurring-problems"
              />
            </ChakraBox>
          )}
          {coOccurringProblems.length > 0 && (
            <ChakraFlex
              borderRadius="12px"
              borderWidth="1px"
              direction="column"
              marginBottom="16px"
              marginTop="12px"
              padding="18px"
              rowGap="16px"
            >
              {coOccurringProblems.map((problem) => (
                <PatientNoteItemCarePlanGoal
                  key={problem.id}
                  problem={problem}
                />
              ))}
            </ChakraFlex>
          )}
        </>
      )}
      {shouldShowQolGoalsSection && (
        <>
          <Text variant="bodyMedium">Quality of life goals</Text>
          <ChakraFlex
            borderRadius="12px"
            borderWidth="1px"
            direction="column"
            marginBottom="16px"
            marginTop="12px"
            padding="18px"
            rowGap="16px"
          >
            {carePlan.qolGoals.map((qolGoal) => (
              <PatientNoteItemCarePlanQolGoal
                key={qolGoal.id}
                qolGoal={qolGoal}
              />
            ))}
          </ChakraFlex>
        </>
      )}
      {showInterventions && interventions.length > 0 && (
        <>
          <Text variant="bodyMedium">Interventions</Text>
          <ChakraGrid
            templateColumns="minmax(0, 1fr) auto"
            borderRadius="12px"
            borderWidth="1px"
            marginTop="12px"
            marginBottom="16px"
            padding="18px"
            rowGap="12px"
            columnGap="24px"
          >
            {interventions.map((intervention) => (
              <PatientNoteItemInterventionRow
                key={intervention.id}
                intervention={intervention}
              />
            ))}
          </ChakraGrid>
        </>
      )}
    </PatientReadonlyNoteCollapse>
  );
};
